/* -----------------Globals--------------- */
import { useCallback } from "react";

/* -----------------Constants--------------- */
import { normalizeProethicaSessionResponse } from "@constants/demo";
import {
	canEnterProethicaReview,
	getProethicaTestByCollectionId,
} from "@constants/demo/proethica-tests";
import type { ProethicaModuleSummary } from "@constants/demo/session-collection/proethica";
import { events } from "@constants/tracking";
/* -----------------Helpers & Hooks--------------- */
import { hydrateProethicsReview } from "@features/proethics-simulation/helpers/reviewHydration";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { flattenSessionItems } from "@helpers/session";
import type { BackendSessionResponse } from "@memorang/types/src/session/new-session";
/* -----------------Router--------------- */
import { useQueryClient } from "@tanstack/react-query";
import { trackCustomEvent } from "analytics";
import { useRouter } from "expo-router";
import { sessionItemsQueryOptions } from "../api";
import {
	deriveResumeIndexFromAnswerEcho,
	seedAnswerStateFromItems,
} from "../helpers/seed-answer-state";
import { useProethicaSessionStore } from "./useProethicaSessionStore";
import { useProethicaSessionTimeStore } from "./useProethicaSessionTimeStore";
import useSessionStore from "./useSessionStore";

export type ResumeOutcome = "resumed" | "report" | "failed";
type ResumeSessionParams = {
	numericId: number;
	replace?: boolean;
	moduleId?: string;
	reviewMode?: boolean;
	reviewIndex?: number;
};
type Router = ReturnType<typeof useRouter>;
type ResumableSessionResponse = Omit<BackendSessionResponse, "sessionItems"> & {
	sessionItems: BackendSessionResponse["sessionItems"];
	collectionId?: number;
	moduleSummary?: ProethicaModuleSummary;
	status?: string;
};

const isCollectionMismatch = (
	moduleId: string | undefined,
	collectionId: number | undefined,
) => Boolean(moduleId) && String(collectionId) !== moduleId;

const shouldPreserveLocalState = (
	current: ReturnType<typeof useSessionStore.getState>,
	response: { id: number; numericId: number },
) =>
	String(current.id) === String(response.id) ||
	String(current.numericId) === String(response.numericId);

const getResumeProgress = (resumeIndex: number, itemCount: number) =>
	itemCount ? (resumeIndex + 1) / itemCount : 0;

const clampIndex = (index: number | undefined, itemCount: number) => {
	const fallbackIndex = Number.isFinite(index) ? index! : 0;
	const maxIndex = Math.max(itemCount - 1, 0);
	return Math.min(Math.max(fallbackIndex, 0), maxIndex);
};

const getResolvedModuleId = (
	moduleId: string | undefined,
	collectionId: number | undefined,
) => moduleId ?? (collectionId != null ? String(collectionId) : undefined);

const navigateToResumedSession = (
	router: Router,
	replace: boolean,
	numericId: number,
	resumeIndex: number,
) => {
	const navigate = replace ? router.replace : router.push;
	navigate({
		pathname: "/(protected)/session/[id]",
		params: {
			id: String(numericId),
			index: String(resumeIndex + 1),
		},
	});
};

const navigateToReviewSession = (
	router: Router,
	replace: boolean,
	numericId: number,
	reviewIndex: number,
) => {
	const navigate = replace ? router.replace : router.push;
	navigate({
		pathname: "/(protected)/session/[id]/review",
		params: {
			id: String(numericId),
			index: String(reviewIndex + 1),
		},
	});
};

const applyResumedSessionState = ({
	response,
	moduleId,
	reviewMode,
	reviewIndex,
	updateSession,
	updateCurrentItemIndex,
	updateProgress,
}: {
	response: ResumableSessionResponse;
	moduleId?: string;
	reviewMode?: boolean;
	reviewIndex?: number;
	updateSession: ReturnType<typeof useSessionStore.getState>["updateSession"];
	updateCurrentItemIndex: (index: number) => void;
	updateProgress: (progress: number) => void;
}) => {
	const sessionItems = flattenSessionItems(response.sessionItems);
	const itemCount = sessionItems.length;
	// A device that never held this session has no stored index; the echo's
	// lastUpdated marker stands in for it.
	const resumeIndex =
		useProethicaSessionStore
			.getState()
			.getResumeSessionIndex(response.numericId, itemCount) ??
		deriveResumeIndexFromAnswerEcho(sessionItems);
	const current = useSessionStore.getState();
	const isSameSession = shouldPreserveLocalState(current, response);
	const resolvedModuleId = getResolvedModuleId(moduleId, response.collectionId);
	const testDescriptor = resolvedModuleId
		? getProethicaTestByCollectionId(resolvedModuleId)
		: undefined;
	const seeded = isSameSession
		? undefined
		: seedAnswerStateFromItems(sessionItems, Boolean(testDescriptor));
	updateSession({
		sessionResponse: {
			...response,
			sessionItems,
		},
		isSummativeTest: Boolean(testDescriptor),
		// Review applies to completed modules too (no testDescriptor), so gate on
		// reviewMode alone here (it already requires status === COMPLETE upstream).
		inReviewMode: Boolean(reviewMode),
		proethicaTestKind: testDescriptor?.kind,
		moduleSummary: response.moduleSummary,
		moduleId: resolvedModuleId,
		itemStates: isSameSession ? current.itemStates : seeded?.itemStates,
		answers: isSameSession ? current.answers : seeded?.answers,
		reviewedPrincipleKeys: isSameSession
			? current.reviewedPrincipleKeys
			: undefined,
	});
	if (reviewMode) {
		// Grade to reveal correct answers, and rebuild the ProEthics sim runs from
		// persisted data so AI-simulation items replay on a cold review URL instead
		// of rendering blank.
		useSessionStore.getState().gradeItemsForReview(sessionItems);
		hydrateProethicsReview(sessionItems);
	} else if (!testDescriptor) {
		// updateSession zeroed timeElapsed; a same-device module resume continues
		// its wall clock from the persisted value so reported time reflects actual
		// working time across the pause.
		const persistedElapsed = useProethicaSessionTimeStore
			.getState()
			.getSessionElapsed(response.id, response.numericId);
		if (persistedElapsed != null) {
			useSessionStore.getState().updateTimeElapsed(persistedElapsed);
		}
	}
	const targetIndex = reviewMode
		? clampIndex(reviewIndex, itemCount)
		: resumeIndex;
	updateCurrentItemIndex(targetIndex);
	updateProgress(getResumeProgress(targetIndex, itemCount));

	return { resumeIndex, targetIndex };
};

export const useResumeSession = () => {
	const router = useRouter();
	const queryClient = useQueryClient();
	const updateSession = useSessionStore((state) => state.updateSession);
	const updateCurrentItemIndex = useSessionStore(
		(state) => state.updateCurrentItemIndex,
	);
	const updateProgress = useSessionStore((state) => state.updateProgress);

	const handleResumeSession = useCallback(
		async ({
			numericId,
			replace = false,
			moduleId,
			reviewMode,
			reviewIndex,
		}: ResumeSessionParams): Promise<ResumeOutcome> => {
			try {
				const response = normalizeProethicaSessionResponse(
					await queryClient.fetchQuery(sessionItemsQueryOptions(numericId)),
				);

				if (isCollectionMismatch(moduleId, response.collectionId)) {
					captureErrorToSentry(
						new Error("Tracked session collectionId mismatch on resume"),
						{ moduleId, responseCollectionId: String(response.collectionId) },
					);
					return "failed";
				}

				// Review is only valid for finished sessions; a stale/crafted review
				// URL on an in-progress test must not grade items and reveal the
				// correct answers - fall through to a normal resume instead. A
				// completed pre-test also refuses review (it would leak the shared
				// question pool) and falls through to its report below.
				const resolvedModuleId = getResolvedModuleId(
					moduleId,
					response.collectionId,
				);
				const reviewTestKind = resolvedModuleId
					? getProethicaTestByCollectionId(resolvedModuleId)?.kind
					: undefined;
				const effectiveReviewMode =
					Boolean(reviewMode) &&
					response.status === "COMPLETE" &&
					canEnterProethicaReview(reviewTestKind);

				if (response.status === "COMPLETE" && !effectiveReviewMode) {
					useProethicaSessionStore
						.getState()
						.clearResumeSessionIndex(numericId);
					// No store reset here: the caller's report screen may still be
					// mounted and renders from the store; the report route's hydrator
					// adopts the target session after navigation.
					router.replace(`/session/${numericId}/report/`);
					return "report";
				}

				if (!response.sessionItems?.length) {
					return "failed";
				}

				trackCustomEvent({ eventName: events.sessionResumed, numericId });

				const { resumeIndex, targetIndex } = applyResumedSessionState({
					response,
					moduleId,
					reviewMode: effectiveReviewMode,
					reviewIndex,
					updateSession,
					updateCurrentItemIndex,
					updateProgress,
				});
				if (effectiveReviewMode) {
					navigateToReviewSession(
						router,
						replace,
						response.numericId,
						targetIndex,
					);
					return "resumed";
				}
				navigateToResumedSession(
					router,
					replace,
					response.numericId,
					resumeIndex,
				);
				return "resumed";
			} catch (error) {
				captureErrorToSentry(new Error("Error resuming session"), {
					error: JSON.stringify(error),
				});
				return "failed";
			}
		},
		[
			router,
			updateCurrentItemIndex,
			updateProgress,
			updateSession,
			queryClient,
		],
	);

	return {
		handleResumeSession,
	};
};
